import dayjs from 'dayjs'
import { ReactElement } from 'react'
import { sortBy } from 'ramda'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Divider,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'src/store/hooks'
import { selectAssignmentSummariesV4 } from 'src/v3/manage/manage.slice'

type CreatedAssignmentsModalProps = {
  isOpen: boolean
  onClose: () => void
  assignmentIDs: string[]
  scheduledAssignmentName: string
}

export default function CreatedAssignmentsModal({
  isOpen,
  onClose,
  assignmentIDs,
  scheduledAssignmentName,
}: CreatedAssignmentsModalProps): ReactElement {
  let navigate = useNavigate()
  let allAssignments = useAppSelector(selectAssignmentSummariesV4)

  let handleAssignmentClick = (assignmentID: string) => {
    navigate(`/manage/assignments/${assignmentID}`)
    onClose()
  }

  let foundAssignments = assignmentIDs
    .map((id) => allAssignments.find((a) => a.id === id))
    .filter((a): a is NonNullable<typeof a> => a !== undefined)

  let assignments = sortBy((a) => dayjs(a.dueDate).valueOf(), foundAssignments)

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Assignments Created</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {scheduledAssignmentName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {assignments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No assignments have been created yet.
          </Typography>
        ) : (
          <List disablePadding>
            {assignments.map((assignment, index) => (
              <Box key={assignment.id}>
                {index > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleAssignmentClick(assignment.id)}
                  >
                    <ListItemText
                      primary={assignment.name}
                      secondary={`Due: ${dayjs(assignment.dueDate).format('MMMM D, YYYY')}`}
                    />
                  </ListItemButton>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  )
}
